import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  TagIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CalendarIcon,
  XMarkIcon,
  BuildingLibraryIcon,
  BanknotesIcon,
  HeartIcon,
  CreditCardIcon,
  WalletIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
  DocumentIcon,
  PlusIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
  CurrencyEuroIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  PencilIcon,
  GiftIcon,
  ShoppingCartIcon,
  HomeIcon,
  TruckIcon,
  BriefcaseIcon,
  QuestionMarkCircleIcon,
  ChartBarIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  EllipsisHorizontalIcon,
  BoltIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ExcelExportModal from '../components/ExcelExportModal';
import CSVImporter from '../components/CSVImporter';
import ConfirmationModal from '../components/ConfirmationModal';
import AddBookingModal from '../components/AddBookingModal';
import StatementDetailModal from '../components/modals/StatementDetailModal';
import RuecklagenCard from '../components/RuecklagenCard';

interface Account {
  id: number;
  name: string;
  kontotyp: string;
  beschreibung: string;
  waehrung: string;
  startbetrag: number;
  deactivated: number;
  icon: string;
  ist_gaestekarten_konto?: number;
  ist_verzehrkosten_konto?: number;
  erstellt_am?: string;
  currentBalance?: number;
  recentTransactions?: Transaction[];
}

interface Category {
  category: string;
  income: number;
  expense: number;
  transactionCount: number;
  icon: string | null;
}

interface Transaction {
  id: number;
  buchungstag: string;
  buchungstext: string;
  betrag: number;
  categoryAmount?: number;
  isIncome?: boolean;
  konto_name?: string;
  category?: string;
}

interface CategoryTransactions {
  [key: string]: number[];
}

const CARD_BG = '#1c2432';

// Add CSS animation styles
const animationStyles = `
  @keyframes slideInFromBottom {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const AccountOverviewEnhanced: React.FC = () => {
  const { user } = useContext(AuthContext);
  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [, setCategoryTransactions] = useState<CategoryTransactions>({});
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [availableYears, setAvailableYears] = useState<number[]>([currentYear]);
  const [allStatements, setAllStatements] = useState<any[]>([]);
  const [selectedStatementId, setSelectedStatementId] = useState<string | null>(null);

  // Use availableYears from API instead of calculating from allStatements
  const years = useMemo(() => {
    return availableYears.length > 0 ? availableYears : [currentYear];
  }, [availableYears, currentYear]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant?: 'single' | 'two';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountBalance, setNewAccountBalance] = useState('');
  const [newAccountType, setNewAccountType] = useState('Bargeldkonto');
  const [newAccountIcon, setNewAccountIcon] = useState('FaWallet');
  const [newAccountIsGaestekarten, setNewAccountIsGaestekarten] = useState(false);
  const [newAccountIsVerzehrkosten, setNewAccountIsVerzehrkosten] = useState(false);

  // Prevent background scroll when any modal is open
  useEffect(() => {
    const anyModalOpen = showTransactionModal || showAccountModal || showExportModal || showImportModal || showAddBookingModal || showCreateAccountModal || showEditAccountModal || confirmModal.isOpen;
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showTransactionModal, showAccountModal, showExportModal, showImportModal, showAddBookingModal, showCreateAccountModal, showEditAccountModal, confirmModal.isOpen]);

  // Available icons for accounts
  const availableIcons = [
    { id: 'FaLandmark', name: 'Bank', IconComponent: BuildingLibraryIcon, color: 'green' },
    { id: 'FaWallet', name: 'Portemonnaie', IconComponent: WalletIcon, color: 'red' },
    { id: 'FaCreditCard', name: 'Kreditkarte', IconComponent: CreditCardIcon, color: 'green' },
    { id: 'FaPiggyBank', name: 'Sparkonto', IconComponent: BanknotesIcon, color: 'red' },
    { id: 'FaChartLine', name: 'Depot', IconComponent: ChartBarIcon, color: 'green' },
    { id: 'FaFileInvoiceDollar', name: 'Rechnungen', IconComponent: DocumentTextIcon, color: 'green' },
    { id: 'FaHome', name: 'Zuhause', IconComponent: HomeIcon, color: 'green' },
    { id: 'FaCar', name: 'Auto', IconComponent: TruckIcon, color: 'red' },
    { id: 'FaShoppingCart', name: 'Einkaufen', IconComponent: ShoppingCartIcon, color: 'red' },
    { id: 'FaBriefcase', name: 'Geschäftlich', IconComponent: BriefcaseIcon, color: 'green' },
    { id: 'FaGift', name: 'Geschenke', IconComponent: GiftIcon, color: 'red' },
    { id: 'FaUtensils', name: 'Essen/Kantine', IconComponent: ShoppingCartIcon, color: 'green' },
    { id: 'FaUsers', name: 'Gruppe/Gäste', IconComponent: UserIcon, color: 'green' },
    { id: 'FaStore', name: 'Laden/Kasse', IconComponent: ShoppingCartIcon, color: 'red' },
    { id: 'FaQuestionCircle', name: 'Sonstiges', IconComponent: QuestionMarkCircleIcon, color: 'gray' },
  ];

  // Icon mapping for accounts
  const accountIconMap: { [key: string]: React.ComponentType<any> } = {
    FaUniversity: BuildingLibraryIcon,
    FaLandmark: BuildingLibraryIcon,
    FaUtensils: ShoppingCartIcon,
    FaHeart: HeartIcon,
    FaWallet: WalletIcon,
    FaCreditCard: CreditCardIcon,
    FaPiggyBank: BanknotesIcon,
    FaChartLine: ChartBarIcon,
    FaFileInvoiceDollar: DocumentTextIcon,
    FaHome: HomeIcon,
    FaCar: TruckIcon,
    FaShoppingCart: ShoppingCartIcon,
    FaBriefcase: BriefcaseIcon,
    FaGift: GiftIcon,
    FaUsers: UserIcon,
    FaStore: ShoppingCartIcon,
    FaQuestionCircle: QuestionMarkCircleIcon,
  };

  // OPTIMIZED: Fetch all data in one API call
  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      setIsLoadingAccounts(true);

      const response = await apiClient.get(`/api/finance/account-overview-data?year=${selectedYear}`);

      if (response.data.success) {
        // Set accounts with currentBalance and recentTransactions
        setAccounts(response.data.accounts);

        // Set categories
        setCategories(response.data.categories);

        // Set available years
        setAvailableYears(response.data.availableYears);

        setError(null);
      }
    } catch (err) {
      console.error('Error fetching account overview data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
      setIsLoadingAccounts(false);
    }
  };

  // Fallback: Fetch all statements for export (only when needed)
  const fetchAllStatements = async () => {
    try {
      if (accounts.length === 0) {
        console.log('No accounts available yet');
        return;
      }

      // Fetch statements for each account (like in original AccountStatementsList)
      const statementsWithOverview = await Promise.all(
        accounts.map(async (acc) => {
          try {
            const resStatements = await apiClient.get(`/api/finance/statements?account_id=${acc.id}`);
            const statementsData = resStatements.data || [];

            // Add start statement (Eröffnungsbetrag) like in original
            const startStatement = {
              id: `${acc.id}-start`,
              originalId: 'start',
              buchungstag: acc.erstellt_am ? new Date(acc.erstellt_am).toISOString().split('T')[0] : '1970-01-01',
              buchungstext: 'Eröffnungsbetrag',
              verwendungszweck: 'Kontostand zu Beginn',
              name_zahlungsbeteiligter: '',
              betrag: acc.startbetrag || 0,
              konto_id: acc.id,
              kontotyp: acc.kontotyp || 'Sonstiges',
              documents: [],
              categories: [],
            };

            // Enrich statements with additional data
            const enrichedStatements = [
              startStatement,
              ...statementsData.map((stmt: any) => ({
                ...stmt,
                originalId: stmt.id?.toString() || stmt.id,
                id: `${acc.id}-${stmt.id}`,
                konto_id: acc.id,
                kontotyp: acc.kontotyp || 'Sonstiges',
                documents: stmt.documents || [],
                name_zahlungsbeteiligter: stmt.name_zahlungsbeteiligter || '',
                verwendungszweck: stmt.verwendungszweck || '',
                buchungstext: stmt.buchungstext || '',
                betrag: parseFloat(stmt.betrag) || 0,
              })),
            ];

            return enrichedStatements;
          } catch (err) {
            console.error(`Error fetching statements for account ${acc.id}:`, err);
            return [];
          }
        })
      );

      const allStatementsFlat = statementsWithOverview.flat();
      const sorted = allStatementsFlat.sort((a, b) => new Date(b.buchungstag).getTime() - new Date(a.buchungstag).getTime());
      setAllStatements(sorted);
    } catch (error) {
      console.error('Error fetching all statements:', error);
    }
  };

  // Handle account functions
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAccountName.trim()) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Kontoname ist erforderlich.',
        variant: 'single'
      });
      return;
    }
    try {
      const response = await apiClient.post('/api/finance/accounts', {
        name: newAccountName.trim(),
        startbetrag: parseFloat(newAccountBalance) || 0,
        kontotyp: newAccountType,
        icon: newAccountIcon,
        beschreibung: '',
        waehrung: 'EUR',
        ist_gaestekarten_konto: newAccountIsGaestekarten,
        ist_verzehrkosten_konto: newAccountIsVerzehrkosten
      });
      await fetchAllData();
      setShowCreateAccountModal(false);
      setNewAccountName('');
      setNewAccountBalance('');
      setNewAccountType('Bargeldkonto');
      setNewAccountIcon('FaWallet');
      setNewAccountIsGaestekarten(false);
      setNewAccountIsVerzehrkosten(false);
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolg',
        message: 'Konto wurde erfolgreich erstellt.',
        variant: 'single'
      });
      return response;
    } catch (error) {
      console.error('Fehler beim Erstellen des Kontos:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Konto konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
        variant: 'single'
      });
    }
  };

  const handleDeleteAccount = async (accountId: number) => {
    try {
      await apiClient.delete(`/api/finance/accounts/${accountId}`);
      await fetchAllData();
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolg',
        message: 'Konto wurde erfolgreich gelöscht.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Fehler beim Löschen des Kontos:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Konto konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.',
        variant: 'single'
      });
    }
  };

  const handleEditAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAccount) return;

    try {
      await apiClient.put(`/api/finance/accounts/${editingAccount.id}`, {
        name: newAccountName,
        kontotyp: newAccountType,
        beschreibung: editingAccount.beschreibung,
        waehrung: editingAccount.waehrung,
        startbetrag: editingAccount.startbetrag,
        icon: newAccountIcon,
        ist_gaestekarten_konto: newAccountIsGaestekarten ? 1 : 0,
        ist_verzehrkosten_konto: newAccountIsVerzehrkosten ? 1 : 0
      });

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: `Konto "${newAccountName}" wurde erfolgreich aktualisiert.`,
        variant: 'single'
      });

      setShowEditAccountModal(false);
      setEditingAccount(null);
      setNewAccountName('');
      setNewAccountType('Bargeldkonto');
      setNewAccountIcon('FaWallet');
      setNewAccountIsGaestekarten(false);
      setNewAccountIsVerzehrkosten(false);
      fetchAllData();
    } catch (error) {
      console.error('Error updating account:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Konto konnte nicht aktualisiert werden.',
        variant: 'single'
      });
    }
  };

  const openEditModal = (account: Account) => {
    setEditingAccount(account);
    setNewAccountName(account.name);
    setNewAccountType(account.kontotyp);
    setNewAccountIcon(account.icon || 'FaWallet');
    setNewAccountIsGaestekarten(account.ist_gaestekarten_konto === 1);
    setNewAccountIsVerzehrkosten(account.ist_verzehrkosten_konto === 1);
    setShowEditAccountModal(true);
  };

  const handleDeactivateAccount = async (accountId: number) => {
    try {
      await apiClient.patch(`/api/finance/accounts/${accountId}`, {
        deactivated: 1
      });
      await fetchAllData();
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolg',
        message: 'Konto wurde erfolgreich deaktiviert.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Fehler beim Deaktivieren des Kontos:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Konto konnte nicht deaktiviert werden. Bitte versuchen Sie es erneut.',
        variant: 'single'
      });
    }
  };

  const handleActivateAccount = async (accountId: number) => {
    try {
      await apiClient.patch(`/api/finance/accounts/${accountId}`, {
        deactivated: 0
      });
      await fetchAllData();
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolg',
        message: 'Konto wurde erfolgreich aktiviert.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Fehler beim Aktivieren des Kontos:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Konto konnte nicht aktiviert werden. Bitte versuchen Sie es erneut.',
        variant: 'single'
      });
    }
  };

  // Fetch accounts
  const fetchAccounts = async () => {
    try {
      setIsLoadingAccounts(true);
      const response = await apiClient.get('/api/finance/accounts');

      if (Array.isArray(response.data)) {
        const accountsData = response.data;

        // Fetch recent transactions for each account
        const accountsWithTransactions = await Promise.all(
          accountsData.map(async (account: Account) => {
            try {
              const statementsResponse = await apiClient.get(
                `/api/finance/statements?account_id=${account.id}&limit=5`
              );

              let currentBalance = account.startbetrag;
              let recentTransactions: Transaction[] = [];

              if (statementsResponse.data && Array.isArray(statementsResponse.data)) {
                recentTransactions = statementsResponse.data.slice(0, 5);
                // Calculate current balance from all transactions
                const allTransactions = statementsResponse.data;
                currentBalance = allTransactions.reduce((sum: number, txn: any) => {
                  return sum + (parseFloat(txn.betrag) || 0);
                }, account.startbetrag);
              }

              return {
                ...account,
                currentBalance,
                recentTransactions,
              };
            } catch (err) {
              console.error(`Error fetching transactions for account ${account.id}:`, err);
              return {
                ...account,
                currentBalance: account.startbetrag,
                recentTransactions: [],
              };
            }
          })
        );

        setAccounts(accountsWithTransactions);
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Fehler beim Laden der Konten');
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get(`/api/finance/account-categories?year=${selectedYear}`);

      if (response.data.success) {
        console.log(response.data);
        const normalizedCategories = response.data.categories.map((cat: any) => ({
          category: cat.category,
          income: parseFloat(cat.income) || 0,
          expense: Math.abs(parseFloat(cat.expense)) || 0,
          transactionCount: cat.transactionCount || 0,
          icon: cat.icon || 'TagIcon',
        }));
        setCategories(normalizedCategories);
        setError(null);

        // Fetch transaction timelines for all categories
        await fetchAllCategoryTransactions(normalizedCategories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Fehler beim Laden der Kategorien');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch transaction timeline for all categories
  const fetchAllCategoryTransactions = async (categories: Category[]) => {
    setIsLoadingTransactions(true);
    try {
      const transactionPromises = categories.map(async (cat) => {
        try {
          const response = await apiClient.get(
            `/api/finance/account-categories/${encodeURIComponent(cat.category)}/${selectedYear}`
          );

          if (response.data.success && Array.isArray(response.data.transactions)) {
            const bookingMonths = response.data.transactions.reduce((acc: number[], txn: any) => {
              const date = new Date(txn.buchungstag);
              const month = date.getMonth();
              const monthPosition = (month / 12) * 100;
              if (!acc.includes(monthPosition)) {
                acc.push(monthPosition);
              }
              return acc;
            }, []).sort((a: number, b: number) => a - b);

            return { category: cat.category, bookingMonths };
          }
          return { category: cat.category, bookingMonths: [] };
        } catch (error) {
          console.error(`Error fetching transactions for ${cat.category}:`, error);
          return { category: cat.category, bookingMonths: [] };
        }
      });

      const results = await Promise.all(transactionPromises);
      const newCategoryTransactions = results.reduce((acc, { category, bookingMonths }) => {
        acc[category] = bookingMonths;
        return acc;
      }, {} as CategoryTransactions);

      setCategoryTransactions(newCategoryTransactions);
    } catch (error) {
      console.error('Error fetching transaction timelines:', error);
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  // Handle category click
  const handleCategoryClick = async (category: string) => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(
        `/api/finance/account-categories/${encodeURIComponent(category)}/${selectedYear}`
      );

      if (response.data.success && Array.isArray(response.data.transactions)) {
        setSelectedCategory(category);
        setTransactions(response.data.transactions);
        setShowTransactionModal(true);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Fehler beim Laden der Transaktionen');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle account click
  const handleAccountClick = async (account: Account) => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/api/finance/statements?account_id=${account.id}`);

      if (Array.isArray(response.data)) {
        setSelectedAccount(account);
        setTransactions(response.data);
        setShowAccountModal(true);
      }
    } catch (error) {
      console.error('Error fetching account transactions:', error);
      setError('Fehler beim Laden der Kontobuchungen');
    } finally {
      setIsLoading(false);
    }
  };

  // OPTIMIZED: Fetch all data in one API call when year changes
  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Removed: Old separate fetchAccounts() + fetchCategories() calls
  // (kept fetchAccounts/fetchCategories defined for potential future use)
  void fetchAccounts;
  void fetchCategories;

  // Loading state for export modal
  const [isLoadingExport, setIsLoadingExport] = useState(false);

  // Open export modal with data
  const handleOpenExportModal = async () => {
    setShowExportModal(true);
    setIsLoadingExport(true);
    await fetchAllStatements();
    setIsLoadingExport(false);
  };

  // Calculate totals
  const totalBalance = accounts.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
  const totalIncome = categories.reduce((sum, cat) => sum + cat.income, 0);
  const totalExpense = categories.reduce((sum, cat) => sum + cat.expense, 0);
  const saldo = totalIncome - totalExpense;

  // Calculate statistics
  const transactionCount = categories.reduce((sum, cat) => sum + cat.transactionCount, 0);

  // Calculate uncategorized transactions
  const uncategorizedCount = categories.find(cat =>
    cat.category === 'Unkategorisiert' ||
    cat.category === 'Nicht kategorisiert' ||
    cat.category === 'Uncategorized' ||
    cat.category === ''
  )?.transactionCount || 0;

  const uncategorizedAmount = categories.find(cat =>
    cat.category === 'Unkategorisiert' ||
    cat.category === 'Nicht kategorisiert' ||
    cat.category === 'Uncategorized' ||
    cat.category === ''
  );
  const uncategorizedTotal = (uncategorizedAmount?.income || 0) + (uncategorizedAmount?.expense || 0);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Format currency compact for mobile
  const formatCurrencyCompact = (amount: number) => {
    const absAmount = Math.abs(amount);
    if (absAmount >= 1000) {
      return `${(amount / 1000).toFixed(1)}k`;
    } else if (absAmount >= 100) {
      return `${Math.round(amount)}€`;
    }
    return formatCurrency(amount);
  };
  void formatCurrencyCompact;

  // Format date
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Get account icon details
  const getAccountIconDetails = (account: Account) => {
    const defaultIconData = availableIcons.find(i => i.id === 'FaQuestionCircle') || { id: 'FaQuestionCircle', IconComponent: QuestionMarkCircleIcon, color: 'gray' };
    let iconDetail = defaultIconData;

    if (account && account.icon) {
      const foundIcon = availableIcons.find(icon => icon.id === account.icon);
      if (foundIcon) iconDetail = foundIcon;
    } else if (account && account.kontotyp) {
      const type = account.kontotyp.toLowerCase();
      if (type.includes('giro') || type.includes('bank')) iconDetail = availableIcons.find(i => i.id === 'FaLandmark') || defaultIconData;
      else if (type.includes('portemonnaie') || type.includes('bargeld') || type.includes('kasse')) iconDetail = availableIcons.find(i => i.id === 'FaWallet') || defaultIconData;
      else if (type.includes('kreditkarte')) iconDetail = availableIcons.find(i => i.id === 'FaCreditCard') || defaultIconData;
      else if (type.includes('spar')) iconDetail = availableIcons.find(i => i.id === 'FaPiggyBank') || defaultIconData;
      else if (type.includes('depot')) iconDetail = availableIcons.find(i => i.id === 'FaChartLine') || defaultIconData;
      else if (type.includes('kantine')) iconDetail = availableIcons.find(i => i.id === 'FaUtensils') || defaultIconData;
    }

    const IconComponent = iconDetail.IconComponent;
    const colorName = iconDetail.color;

    return { IconComponent, colorName };
  };

  // Get account icon (backward compatibility)
  const getAccountIcon = (iconName: string) => {
    const IconComponent = accountIconMap[iconName] || WalletIcon;
    return <IconComponent className="h-6 w-6" />;
  };

  // Category Icon Map
  const categoryIconMap: { [key: string]: any } = {
    'FaTrash': TrashIcon,
    'FaBuilding': BuildingLibraryIcon,
    'FaUsersCog': UsersIcon,
    'FaCar': TruckIcon,
    'FaUserPlus': UserPlusIcon,
    'FaDog': HeartIcon, // Using HeartIcon for dogs
    'FaTools': WrenchScrewdriverIcon,
    'FaUtensils': ShoppingCartIcon,
    'FaUsers': UsersIcon,
    'FaEllipsisH': EllipsisHorizontalIcon,
    'FaEuroSign': CurrencyEuroIcon,
    'FaHeart': HeartIcon,
    'FaBolt': BoltIcon,
    'FaBookOpen': BookOpenIcon,
    'FaShieldAlt': ShieldCheckIcon,
    'FaTint': BeakerIcon,
  };

  // Get category icon
  const getCategoryIcon = (iconName: string | null) => {
    if (!iconName) return TagIcon;
    return categoryIconMap[iconName] || TagIcon;
  };
  void getCategoryIcon;

  // Sort categories by amount (largest first)
  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const aMax = Math.max(a.income, a.expense);
      const bMax = Math.max(b.income, b.expense);
      return bMax - aMax;
    });
  }, [categories]);

  // Story-Date label
  const monthYear = useMemo(
    () => new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
    []
  );

  // Largest income / expense category for story
  const topIncomeCat = useMemo(() => {
    return [...categories]
      .filter(c => c.income > 0)
      .sort((a, b) => b.income - a.income)[0];
  }, [categories]);
  const topExpenseCat = useMemo(() => {
    return [...categories]
      .filter(c => c.expense > 0)
      .sort((a, b) => b.expense - a.expense)[0];
  }, [categories]);

  // Account stats
  const activeAccounts = accounts.filter(a => !a.deactivated);
  const negativeAccounts = activeAccounts.filter(a => (a.currentBalance || 0) < 0);
  const hasGaestekartenKonto = accounts.some(acc => acc.ist_gaestekarten_konto === 1);
  const hasVerzehrkostenKonto = accounts.some(acc => acc.ist_verzehrkosten_konto === 1);
  const missingDefaultAccounts = accounts.length > 0 && (!hasGaestekartenKonto || !hasVerzehrkostenKonto);

  // Saldo % vs total income
  const saldoPct = totalIncome > 0 ? Math.round((saldo / totalIncome) * 100) : 0;
  const saldoRingDeg = Math.max(0, Math.min(360, (Math.abs(saldoPct) / 100) * 360));
  const saldoColor = saldo >= 0 ? '#34d399' : '#f43f5e';

  // Build Story-Copy paragraph dynamically
  const renderStory = (): React.ReactNode => {
    if (accounts.length === 0) {
      return <>Noch keine Konten angelegt — leg jetzt los und richte dein erstes Konto ein.</>;
    }
    return (
      <>
        <span className="font-bold text-gray-900 dark:text-white">{activeAccounts.length} {activeAccounts.length === 1 ? 'aktives Konto' : 'aktive Konten'}</span>
        {' · '}
        <span className="font-bold text-gray-900 dark:text-white tabular-nums">
          {formatCurrency(totalBalance)} Bestand
        </span>
        {totalIncome > 0 && (
          <>
            {'. '}
            <span className="font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{formatCurrency(totalIncome)} Einnahmen</span>
            {' stehen '}
            <span className="font-bold text-rose-700 dark:text-rose-300 tabular-nums">{formatCurrency(totalExpense)} Ausgaben</span>
            {' gegenüber'}
          </>
        )}
        {topExpenseCat && totalExpense > 0 && (
          <>
            {'. Größter Ausgabenposten: '}
            <span className="font-bold text-gray-900 dark:text-white">{topExpenseCat.category || 'Unkategorisiert'}</span>
            {' mit '}
            <span className="font-bold text-gray-900 dark:text-white tabular-nums">{formatCurrency(topExpenseCat.expense)}</span>
          </>
        )}
        {uncategorizedCount > 0 && (
          <>
            {'. '}
            <span className="font-bold text-amber-700 dark:text-amber-300">
              {uncategorizedCount} {uncategorizedCount === 1 ? 'Buchung' : 'Buchungen'} ohne Kategorie
            </span>
            {uncategorizedTotal !== 0 && (
              <> <span className="text-gray-500 dark:text-slate-500 tabular-nums">({formatCurrency(Math.abs(uncategorizedTotal))})</span></>
            )}
          </>
        )}
        .
      </>
    );
  };

  // Has urgent action
  const hasUrgent = uncategorizedCount > 0 || negativeAccounts.length > 0 || missingDefaultAccounts;

  // Story snippet helpers for chart bars
  const renderChartBar = (label: string, value: number, max: number, color: 'primary' | 'rose' | 'amber' | 'blue', onClick?: () => void) => {
    const pct = max > 0 ? (value / max) * 100 : 0;
    const colorMap = {
      primary: { rgba1: 'rgba(16,185,129,0.40)', rgba2: 'rgba(16,185,129,0.20)', border: 'rgba(16,185,129,0.50)' },
      rose: { rgba1: 'rgba(244,63,94,0.40)', rgba2: 'rgba(244,63,94,0.20)', border: 'rgba(244,63,94,0.50)' },
      amber: { rgba1: 'rgba(245,158,11,0.40)', rgba2: 'rgba(245,158,11,0.20)', border: 'rgba(245,158,11,0.50)' },
      blue: { rgba1: 'rgba(59,130,246,0.40)', rgba2: 'rgba(59,130,246,0.20)', border: 'rgba(59,130,246,0.50)' },
    } as const;
    const c = colorMap[color];
    return (
      <div
        className={`flex items-center gap-3 ${onClick ? 'cursor-pointer hover:bg-white/[0.03]' : ''} px-2 py-1 rounded-lg transition`}
        onClick={onClick}
      >
        <span className="text-[11px] text-gray-700 dark:text-slate-300 w-32 sm:w-44 truncate" title={label}>{label}</span>
        <div className="flex-1 h-7 rounded-full relative overflow-hidden bg-gray-100 dark:bg-white/[0.04]" >
          <div
            className="absolute left-0 top-0 h-full rounded-full transition-all"
            style={{
              width: `${pct}%`,
              background: `linear-gradient(90deg, ${c.rgba1} 0%, ${c.rgba2} 100%)`,
              borderRight: `1px solid ${c.border}`,
            }}
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] font-bold text-gray-900 dark:text-white z-10 tabular-nums">
            {formatCurrency(value)}
          </span>
        </div>
        <span className="text-[10px] text-gray-500 dark:text-slate-500 w-10 text-right tabular-nums">
          {max > 0 ? `${pct.toFixed(0)}%` : '—'}
        </span>
      </div>
    );
  };

  // Reset modal-state helpers
  const resetCreateForm = () => {
    setShowCreateAccountModal(false);
    setNewAccountName('');
    setNewAccountBalance('');
    setNewAccountType('Bargeldkonto');
    setNewAccountIcon('FaWallet');
    setNewAccountIsGaestekarten(false);
    setNewAccountIsVerzehrkosten(false);
  };
  const resetEditForm = () => {
    setShowEditAccountModal(false);
    setEditingAccount(null);
    setNewAccountName('');
    setNewAccountType('Bargeldkonto');
    setNewAccountIcon('FaWallet');
    setNewAccountIsGaestekarten(false);
    setNewAccountIsVerzehrkosten(false);
  };

  return (
    <>
      <style>{animationStyles}</style>
      <div
        className="min-h-screen font-sans text-gray-800 dark:text-slate-200"
        style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">

          {/* INTRO Schriftzug */}
          <section className="pt-2 pb-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-info-400/80 font-semibold mb-3">
              Finanzübersicht · Stand {monthYear}
            </div>
            <h2
              className="text-4xl md:text-6xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.02]"
              style={{ letterSpacing: '-0.04em' }}
            >
              Vereins-<br />
              <span className="text-info-600 dark:text-info-400">Bilanz {selectedYear}.</span>
            </h2>

            {accounts.length > 0 ? (
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-start">
                {/* Saldo (links, groß) */}
                <div className="lg:min-w-[280px]">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-slate-500 font-bold">
                    Saldo {selectedYear}
                  </div>
                  <div className={`text-5xl md:text-7xl font-black tabular-nums leading-none mt-2 ${saldo >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                    {(() => {
                      const sign = saldo >= 0 ? '+' : '−';
                      const [intPart, restPart] = formatCurrency(Math.abs(saldo)).split(',');
                      return (
                        <>
                          {sign}{intPart}
                          <span className={`text-3xl md:text-4xl ${saldo >= 0 ? 'text-emerald-700/70 dark:text-emerald-300/70' : 'text-rose-700/70 dark:text-rose-300/70'}`}>
                            ,{restPart}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Prosa (rechts) */}
                <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
                  {renderStory()}
                </p>
              </div>
            ) : (
              <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
                {renderStory()}
              </p>
            )}
          </section>

          {/* Error Banner */}
          {error && (
            <div className="mb-6 p-4 rounded-2xl flex items-center gap-3 backdrop-blur"
                 style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}>
              <ExclamationTriangleIcon className="h-5 w-5 text-rose-700 dark:text-rose-300 flex-shrink-0" />
              <span className="flex-1 text-sm font-medium text-rose-200">{error}</span>
              <button onClick={() => setError(null)} className="text-rose-700 dark:text-rose-300 hover:text-rose-200">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* HERO Action-Card if urgent */}
          {accounts.length > 0 && hasUrgent && (
            <section className="pb-6">
              <article
                className="rounded-3xl overflow-hidden relative bg-white dark:bg-[#1c2432]"
                style={{ border: `1px solid ${negativeAccounts.length > 0 ? 'rgba(244,63,94,0.30)' : 'rgba(245,158,11,0.30)'}`, boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
              >
                <div
                  className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-2xl pointer-events-none"
                  style={{
                    background: negativeAccounts.length > 0 ? 'rgba(244,63,94,0.10)' : 'rgba(245,158,11,0.10)',
                  }}
                />
                <div className="relative px-5 md:px-7 py-5 flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-6">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                      style={{
                        background: negativeAccounts.length > 0 ? 'rgba(244,63,94,0.15)' : 'rgba(245,158,11,0.15)',
                        border: `1px solid ${negativeAccounts.length > 0 ? 'rgba(244,63,94,0.30)' : 'rgba(245,158,11,0.30)'}`,
                      }}
                    >
                      <ExclamationTriangleIcon className={`w-6 h-6 ${negativeAccounts.length > 0 ? 'text-rose-300' : 'text-amber-300'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[11px] uppercase tracking-[0.18em] font-bold ${negativeAccounts.length > 0 ? 'text-rose-300/90' : 'text-amber-300/90'}`}>
                        Aktion erforderlich
                      </div>
                      <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight leading-tight mt-0.5">
                        {negativeAccounts.length > 0 && (
                          <>{negativeAccounts.length} {negativeAccounts.length === 1 ? 'Konto' : 'Konten'} im Minus</>
                        )}
                        {negativeAccounts.length > 0 && uncategorizedCount > 0 && ' · '}
                        {uncategorizedCount > 0 && (
                          <>{uncategorizedCount} unkategorisiert</>
                        )}
                        {negativeAccounts.length === 0 && uncategorizedCount === 0 && missingDefaultAccounts && (
                          <>Standard-Konten fehlen</>
                        )}
                      </h3>
                      <ul className="mt-2 space-y-1 text-sm text-gray-700 dark:text-slate-300">
                        {negativeAccounts.slice(0, 2).map((a) => (
                          <li key={a.id} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                            <span>
                              <span className="font-bold text-gray-900 dark:text-white">{a.name}</span>
                              {' steht bei '}
                              <span className="font-bold text-rose-700 dark:text-rose-300 tabular-nums">{formatCurrency(a.currentBalance || 0)}</span>
                            </span>
                          </li>
                        ))}
                        {uncategorizedCount > 0 && (
                          <li className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                            <span>
                              <span className="font-bold text-gray-900 dark:text-white">{uncategorizedCount} {uncategorizedCount === 1 ? 'Buchung' : 'Buchungen'}</span>
                              {' brauchen noch eine Kategorie'}
                              {uncategorizedTotal !== 0 && (
                                <> · <span className="tabular-nums text-amber-200">{formatCurrency(Math.abs(uncategorizedTotal))}</span></>
                              )}
                            </span>
                          </li>
                        )}
                        {missingDefaultAccounts && (
                          <li className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                            <span>
                              Standard-Konten fehlen{' '}
                              {!hasGaestekartenKonto && <span className="text-amber-200">· Gästekarten</span>}
                              {!hasVerzehrkostenKonto && <span className="text-amber-200">· Verzehrkosten</span>}
                            </span>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0 self-stretch md:self-auto">
                    {uncategorizedCount > 0 && (
                      <button
                        onClick={() => handleCategoryClick('Unkategorisiert')}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl text-amber-700 dark:text-amber-300 text-xs font-bold transition hover:bg-amber-500/20"
                        style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
                      >
                        Kategorisieren
                      </button>
                    )}
                    <button
                      onClick={() => setShowAddBookingModal(true)}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl text-slate-950 text-xs font-bold bg-primary-500 hover:bg-primary-400 transition"
                    >
                      <PlusIcon className="h-4 w-4" /> Neue Buchung
                    </button>
                  </div>
                </div>
              </article>
            </section>
          )}

          {/* Toolbar (Year-Tabs + Actions) */}
          {accounts.length > 0 && (
            <section className="pb-5">
              <div className="flex flex-wrap items-center gap-2">
                <div
                  className="inline-flex p-1 rounded-2xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  {years.map((year) => (
                    <button
                      key={year}
                      onClick={() => setSelectedYear(year)}
                      className={`px-3 py-2 rounded-xl text-xs font-bold transition ${
                        selectedYear === year
                          ? 'bg-primary-500 text-slate-950'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
                <div className="flex-1" />
                <button
                  onClick={() => setShowImportModal(true)}
                  className="w-10 h-10 flex items-center justify-center rounded-xl text-gray-700 dark:text-slate-300 hover:text-white transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  title="CSV Import"
                >
                  <DocumentIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={handleOpenExportModal}
                  className="w-10 h-10 flex items-center justify-center rounded-xl text-gray-700 dark:text-slate-300 hover:text-white transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  title="Excel Export"
                >
                  <DocumentArrowDownIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setIsEditMode(!isEditMode)}
                  className={`w-10 h-10 flex items-center justify-center rounded-xl transition ${
                    isEditMode ? 'text-rose-300' : 'text-slate-300 hover:text-white'
                  }`}
                  style={
                    isEditMode
                      ? { background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }
                      : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }
                  }
                  title={isEditMode ? 'Fertig' : 'Verwalten'}
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowAddBookingModal(true)}
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 h-10 rounded-xl text-slate-950 text-xs font-bold bg-primary-500 hover:bg-primary-400 transition"
                >
                  <PlusIcon className="h-4 w-4" /> Neue Buchung
                </button>
              </div>
            </section>
          )}

          {/* Welcome / Empty State */}
          {accounts.length === 0 && !isLoadingAccounts && (
            <section className="py-6">
              <div
                className="rounded-3xl p-8 sm:p-12 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
              >
                <div
                  className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                >
                  <BuildingLibraryIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-primary-400/80 font-semibold mb-2">
                  Willkommen
                </div>
                <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                  Lege dein erstes Konto an.
                </h4>
                <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
                  Verwalte alle Vereinskonten, importiere Auszüge per CSV, kategorisiere Buchungen und exportiere deine Bilanz nach Excel.
                </p>

                <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto text-left">
                  {[
                    { num: '1', icon: WalletIcon, title: 'Konten anlegen', desc: 'Giro, Kasse, Kreditkarte, Sparkonto' },
                    { num: '2', icon: DocumentIcon, title: 'CSV importieren', desc: 'Auszüge mehrerer Banken' },
                    { num: '3', icon: TagIcon, title: 'Kategorien zuordnen', desc: 'Automatisch + manuell verfeinern' },
                    { num: '4', icon: DocumentArrowDownIcon, title: 'Excel exportieren', desc: 'Bilanz für Steuer & Vorstand' },
                  ].map((step) => (
                    <div
                      key={step.num}
                      className="px-4 py-3 rounded-2xl flex items-center gap-3"
                      style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-primary-700 dark:text-primary-300 font-black tabular-nums"
                        style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)' }}
                      >
                        {step.num}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                          <step.icon className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                          {step.title}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5">{step.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => {
                    setIsEditMode(true);
                    setShowCreateAccountModal(true);
                  }}
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-primary-500 hover:bg-primary-400 text-slate-950 font-bold transition"
                >
                  <WalletIcon className="h-5 w-5" />
                  Erstes Konto anlegen
                </button>
              </div>
            </section>
          )}

          {/* Konten Liste */}
          {accounts.length > 0 && (
            <section className="pb-6">
              <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 dark:text-slate-500 font-semibold">Konten</div>
                  <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-0.5">
                    {activeAccounts.length} {activeAccounts.length === 1 ? 'Konto' : 'Konten'}
                    {' '}<span className="text-gray-500 dark:text-slate-500">·</span>{' '}
                    <span className={totalBalance >= 0 ? 'text-primary-400' : 'text-rose-300'}>{formatCurrency(totalBalance)}</span>
                  </h3>
                </div>
              </div>

              {isLoadingAccounts ? (
                <div className="rounded-3xl p-10 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                     >
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-400/30 border-t-primary-400 mx-auto"></div>
                  <p className="text-sm text-gray-600 dark:text-slate-400 mt-3">Lade Konten…</p>
                </div>
              ) : (
                <>
                  {/* Desktop / Tablet: Grid */}
                  <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {accounts
                      .filter((account) => !(!isEditMode && account.deactivated))
                      .map((account) => {
                        const { IconComponent } = getAccountIconDetails(account);
                        const isDeactivated = account.deactivated === 1;
                        const balance = account.currentBalance || 0;
                        const isPositive = balance >= 0;
                        return (
                          <div
                            key={account.id}
                            onClick={() => !isDeactivated && handleAccountClick(account)}
                            className={`bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700 ${`relative rounded-2xl p-4 transition group ${
                              !isDeactivated ? 'cursor-pointer hover:border-primary-500/40' : 'opacity-50'
                            }`}`}
                          >
                            {isEditMode && (
                              <div className="absolute top-2 right-2 flex gap-1 z-10">
                                <button
                                  title="Bearbeiten"
                                  onClick={(e) => { e.stopPropagation(); openEditModal(account); }}
                                  className="w-7 h-7 rounded-lg flex items-center justify-center text-blue-700 dark:text-blue-300 hover:bg-blue-500/20 transition"
                                  style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.30)' }}
                                >
                                  <PencilIcon className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  title={isDeactivated ? 'Aktivieren' : 'Löschen/Deaktivieren'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isDeactivated) {
                                      setConfirmModal({ isOpen: true, type: 'question', title: 'Konto aktivieren', message: `Soll das Konto "${account.name}" wieder aktiviert werden?`, variant: 'two', onConfirm: () => handleActivateAccount(account.id) });
                                    } else {
                                      const isDeletable = account.currentBalance === 0 || account.currentBalance === account.startbetrag;
                                      setConfirmModal({ isOpen: true, type: 'warning', title: isDeletable ? 'Konto löschen' : 'Konto deaktivieren', message: isDeletable ? `Soll das Konto "${account.name}" wirklich dauerhaft gelöscht werden?` : `Soll das Konto "${account.name}" deaktiviert werden?`, variant: 'two', onConfirm: () => isDeletable ? handleDeleteAccount(account.id) : handleDeactivateAccount(account.id) });
                                    }
                                  }}
                                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition ${
                                    isDeactivated
                                      ? 'text-emerald-300 hover:bg-emerald-500/20'
                                      : 'text-rose-300 hover:bg-rose-500/20'
                                  }`}
                                  style={
                                    isDeactivated
                                      ? { background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)' }
                                      : { background: 'rgba(244,63,94,0.15)', border: '1px solid rgba(244,63,94,0.30)' }
                                  }
                                >
                                  {isDeactivated ? <ArrowUturnLeftIcon className="h-3.5 w-3.5" /> : (account.currentBalance === 0 || account.currentBalance === account.startbetrag) ? <TrashIcon className="h-3.5 w-3.5" /> : <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
                                </button>
                              </div>
                            )}

                            <div className="flex items-center gap-3 mb-3">
                              <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                                style={
                                  isDeactivated
                                    ? { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }
                                    : { background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }
                                }
                              >
                                <IconComponent className={`h-5 w-5 ${isDeactivated ? 'text-slate-500' : 'text-primary-400'}`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-bold text-gray-900 dark:text-white truncate">{account.name}</div>
                                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 mt-0.5">{account.kontotyp}</div>
                              </div>
                              {(account.ist_gaestekarten_konto === 1 || account.ist_verzehrkosten_konto === 1) && (
                                <span
                                  className="hidden xl:inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
                                  style={{
                                    background: 'rgba(59,130,246,0.10)',
                                    border: '1px solid rgba(59,130,246,0.30)',
                                    color: '#93c5fd',
                                  }}
                                >
                                  {account.ist_gaestekarten_konto === 1 ? 'Gäste' : 'Verzehr'}
                                </span>
                              )}
                            </div>

                            <div className={`text-2xl font-black tabular-nums mb-3 ${
                              isDeactivated ? 'text-slate-500' : isPositive ? 'text-primary-400' : 'text-rose-300'
                            }`}>
                              {formatCurrency(balance)}
                            </div>

                            {!isDeactivated && account.recentTransactions && account.recentTransactions.length > 0 && (
                              <div className="space-y-1 pt-2.5" style={{ borderTop: '1px solid #1e293b' }}>
                                {account.recentTransactions.slice(0, 3).map((txn, idx) => (
                                  <div key={idx} className="flex justify-between gap-2 text-[11px]">
                                    <span className="text-gray-600 dark:text-slate-400 truncate">{txn.buchungstext}</span>
                                    <span
                                      className={`font-bold tabular-nums shrink-0 ${
                                        txn.betrag >= 0 ? 'text-emerald-300' : 'text-rose-300'
                                      }`}
                                    >
                                      {txn.betrag >= 0 ? '+' : ''}{formatCurrency(txn.betrag)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                    {/* New Account Card - Edit Mode */}
                    {isEditMode && (
                      <div
                        className="rounded-2xl p-4 cursor-pointer flex items-center justify-center min-h-[160px] transition hover:border-primary-500/40"
                        style={{
                          background: 'rgba(255,255,255,0.02)',
                          border: '2px dashed #334155',
                        }}
                        onClick={() => setShowCreateAccountModal(true)}
                      >
                        <div className="text-center">
                          <PlusIcon className="h-7 w-7 text-primary-600 dark:text-primary-400 mx-auto mb-1.5" />
                          <p className="text-xs font-bold text-gray-700 dark:text-slate-300">Neues Konto</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Mobile: List */}
                  <div
                    className="md:hidden rounded-2xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  >
                    {accounts
                      .filter((account) => !(!isEditMode && account.deactivated))
                      .map((account, idx, arr) => {
                        const { IconComponent } = getAccountIconDetails(account);
                        const isDeactivated = account.deactivated === 1;
                        const balance = account.currentBalance || 0;
                        return (
                          <div
                            key={account.id}
                            onClick={() => !isDeactivated && handleAccountClick(account)}
                            className={`px-4 py-3 flex items-center gap-3 ${
                              !isDeactivated ? 'cursor-pointer active:bg-white/[0.03]' : 'opacity-50'
                            }`}
                            style={idx < arr.length - 1 ? { borderBottom: '1px solid #1e293b' } : undefined}
                          >
                            <div
                              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                              style={
                                isDeactivated
                                  ? { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }
                                  : { background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }
                              }
                            >
                              <IconComponent className={`h-4 w-4 ${isDeactivated ? 'text-slate-500' : 'text-primary-400'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{account.name}</p>
                              <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 mt-0.5">{account.kontotyp}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <p className={`text-sm font-black tabular-nums ${
                                isDeactivated ? 'text-slate-500' : balance >= 0 ? 'text-primary-400' : 'text-rose-300'
                              }`}>
                                {formatCurrency(balance)}
                              </p>
                              {isEditMode && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openEditModal(account); }}
                                  className="p-1.5 rounded-lg text-blue-700 dark:text-blue-300"
                                  style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)' }}
                                >
                                  <PencilIcon className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    {isEditMode && (
                      <button
                        onClick={() => setShowCreateAccountModal(true)}
                        className="w-full py-3 text-xs font-bold text-primary-600 dark:text-primary-400 hover:bg-white/[0.03] transition"
                        style={{ borderTop: '1px dashed #334155' }}
                      >
                        + Neues Konto
                      </button>
                    )}
                  </div>
                </>
              )}
            </section>
          )}

          {/* Rücklagen */}
          <section className="pb-6">
            <RuecklagenCard isVorstand={user?.vorstand === 1} />
          </section>

          {/* Kategorien */}
          {accounts.length > 0 && (
            <section className="pb-6">
              <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 dark:text-slate-500 font-semibold">
                    Kategorien · {selectedYear}
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-0.5">
                    {sortedCategories.length} Kategorien · {transactionCount} Buchungen
                  </h3>
                </div>
                {/* Saldo-Conic-Ring */}
                {totalIncome > 0 && (
                  <div className="hidden sm:flex items-center gap-3">
                    <div
                      className="relative w-14 h-14 rounded-full"
                      style={{
                        background: `conic-gradient(from 0deg, ${saldoColor} 0deg ${saldoRingDeg}deg, rgba(255,255,255,0.08) ${saldoRingDeg}deg 360deg)`,
                        padding: 3,
                      }}
                    >
                      <div
                        className="w-full h-full rounded-full flex flex-col items-center justify-center bg-white dark:bg-[#1c2432]"
                        style={{ border: '1px solid #1e293b'  }}
                      >
                        <div className="text-[9px] font-bold tabular-nums" style={{ color: saldoColor }}>
                          {saldo >= 0 ? '+' : ''}{Math.abs(saldoPct)}%
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Saldo-Quote</div>
                      <div className={`text-sm font-black tabular-nums ${saldo >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                        {saldo >= 0 ? '+' : ''}{formatCurrency(saldo)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              >
                {isLoadingTransactions ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-400/30 border-t-primary-400"></div>
                    <p className="text-sm text-gray-600 dark:text-slate-400 mt-3">Lade Kategorien…</p>
                  </div>
                ) : sortedCategories.length === 0 ? (
                  <div className="text-center py-8">
                    <div
                      className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                      style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                    >
                      <TagIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
                    </div>
                    <p className="text-base font-bold text-gray-900 dark:text-white">Noch keine Buchungen für {selectedYear}</p>
                    <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">Importiere Auszüge oder erfasse manuelle Buchungen.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Einnahmen */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-primary-400" />
                          <span className="text-[11px] uppercase tracking-[0.18em] text-primary-700 dark:text-primary-300 font-bold">Einnahmen</span>
                        </div>
                        <span className="text-sm font-black text-emerald-700 dark:text-emerald-300 tabular-nums">{formatCurrency(totalIncome)}</span>
                      </div>
                      {sortedCategories.filter((cat) => cat.income > cat.expense).length > 0 ? (
                        <div className="space-y-1">
                          {sortedCategories
                            .filter((cat) => cat.income > cat.expense)
                            .map((cat) =>
                              renderChartBar(
                                cat.category || 'Unkategorisiert',
                                cat.income,
                                totalIncome,
                                'primary',
                                () => handleCategoryClick(cat.category)
                              )
                            )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-slate-500 py-4 text-center italic">Keine Einnahmen</p>
                      )}
                    </div>

                    {/* Ausgaben */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-rose-400" />
                          <span className="text-[11px] uppercase tracking-[0.18em] text-rose-700 dark:text-rose-300 font-bold">Ausgaben</span>
                        </div>
                        <span className="text-sm font-black text-rose-700 dark:text-rose-300 tabular-nums">{formatCurrency(totalExpense)}</span>
                      </div>
                      {sortedCategories.filter((cat) => cat.expense >= cat.income && cat.expense > 0).length > 0 ? (
                        <div className="space-y-1">
                          {sortedCategories
                            .filter((cat) => cat.expense >= cat.income && cat.expense > 0)
                            .map((cat) => {
                              const isUncat =
                                cat.category === 'Unkategorisiert' ||
                                cat.category === 'Nicht kategorisiert' ||
                                cat.category === '' ||
                                !cat.category;
                              return renderChartBar(
                                cat.category || 'Unkategorisiert',
                                cat.expense,
                                totalExpense,
                                isUncat ? 'amber' : 'rose',
                                () => handleCategoryClick(cat.category)
                              );
                            })}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-slate-500 py-4 text-center italic">Keine Ausgaben</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Top-Highlights */}
                {sortedCategories.length > 0 && (topIncomeCat || topExpenseCat) && (
                  <div
                    className="mt-5 pt-5 grid grid-cols-1 sm:grid-cols-2 gap-3"
                    style={{ borderTop: '1px solid #1e293b' }}
                  >
                    {topIncomeCat && (
                      <div
                        className="px-4 py-3 rounded-xl flex items-center gap-3"
                        style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                      >
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                          style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                        >
                          <ArrowUpIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Top-Einnahme</div>
                          <div className="text-sm font-bold text-gray-900 dark:text-white truncate">{topIncomeCat.category || 'Unkategorisiert'}</div>
                        </div>
                        <div className="text-sm font-black text-emerald-700 dark:text-emerald-300 tabular-nums shrink-0">
                          {formatCurrency(topIncomeCat.income)}
                        </div>
                      </div>
                    )}
                    {topExpenseCat && (
                      <div
                        className="px-4 py-3 rounded-xl flex items-center gap-3"
                        style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                      >
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                          style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
                        >
                          <ArrowDownIcon className="h-5 w-5 text-rose-700 dark:text-rose-300" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Top-Ausgabe</div>
                          <div className="text-sm font-bold text-gray-900 dark:text-white truncate">{topExpenseCat.category || 'Unkategorisiert'}</div>
                        </div>
                        <div className="text-sm font-black text-rose-700 dark:text-rose-300 tabular-nums shrink-0">
                          {formatCurrency(topExpenseCat.expense)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Account Transaction Modal */}
          {showAccountModal && selectedAccount && (
            <div
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
              style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
              onClick={() => {
                setShowAccountModal(false);
                setSelectedAccount(null);
                setTransactions([]);
              }}
            >
              <div
                className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-5xl flex flex-col overflow-hidden animate-slideUp bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)', animation: 'slideInFromBottom 0.25s ease' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: '1px solid #1e293b' }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                    >
                      <span className="text-primary-600 dark:text-primary-400">{getAccountIcon(selectedAccount.icon)}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Kontoauszug · {selectedAccount.kontotyp}</div>
                      <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight truncate">{selectedAccount.name}</h2>
                      {selectedAccount.beschreibung && (
                        <p className="text-gray-600 dark:text-slate-400 text-sm mt-0.5 truncate">{selectedAccount.beschreibung}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowAccountModal(false);
                      setSelectedAccount(null);
                      setTransactions([]);
                    }}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                  </button>
                </div>

                <div className="px-6 py-5 overflow-y-auto flex-1">
                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-400/30 border-t-primary-400"></div>
                      <p className="text-sm text-gray-600 dark:text-slate-400 mt-3">Lade Buchungen…</p>
                    </div>
                  ) : transactions.length > 0 ? (
                    <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #1e293b' }}>
                      {transactions.map((txn, idx) => {
                        const date = new Date(txn.buchungstag);
                        const day = date.toLocaleDateString('de-DE', { weekday: 'short' });
                        const dayNum = date.getDate();
                        const month = date.toLocaleDateString('de-DE', { month: 'short' });
                        return (
                          <div
                            key={txn.id}
                            onClick={() => setSelectedStatementId(String(txn.id))}
                            className="px-4 py-3 flex items-center gap-3 cursor-pointer transition"
                            style={{
                              borderBottom: idx < transactions.length - 1 ? '1px solid #1e293b' : undefined,
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            <div
                              className="flex flex-col items-center justify-center shrink-0"
                              style={{
                                width: 56,
                                height: 64,
                                borderRadius: 12,
                                background: txn.betrag >= 0
                                  ? 'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)'
                                  : 'linear-gradient(180deg, rgba(244,63,94,0.16) 0%, rgba(244,63,94,0.04) 100%)',
                                border: txn.betrag >= 0 ? '1px solid rgba(16,185,129,0.25)' : '1px solid rgba(244,63,94,0.25)',
                              }}
                            >
                              <div className={`text-[9px] uppercase tracking-widest font-bold ${txn.betrag >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>{day}</div>
                              <div className="text-xl font-black text-gray-900 dark:text-white leading-none mt-0.5 tabular-nums">{dayNum}</div>
                              <div className="text-[9px] text-gray-600 dark:text-slate-400 mt-0.5">{month}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{txn.buchungstext}</p>
                              <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-600 dark:text-slate-400">
                                <span className="flex items-center gap-1">
                                  <CalendarIcon className="h-3 w-3" />
                                  {formatDate(txn.buchungstag)}
                                </span>
                                {txn.category && (
                                  <span className="flex items-center gap-1">
                                    <TagIcon className="h-3 w-3" />
                                    {txn.category}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className={`text-base font-black tabular-nums flex items-center gap-1 ${txn.betrag >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                                {txn.betrag >= 0 ? <ArrowUpIcon className="h-3.5 w-3.5" /> : <ArrowDownIcon className="h-3.5 w-3.5" />}
                                {formatCurrency(Math.abs(txn.betrag))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div
                        className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                        style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                      >
                        <WalletIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                      </div>
                      <p className="text-base font-bold text-gray-900 dark:text-white">Keine Buchungen</p>
                      <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">Für dieses Konto liegen noch keine Buchungen vor.</p>
                    </div>
                  )}
                </div>

                <div
                  className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3"
                  style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Aktueller Saldo</div>
                    <div className={`text-2xl font-black tabular-nums ${(selectedAccount.currentBalance || 0) >= 0 ? 'text-primary-400' : 'text-rose-300'}`}>
                      {formatCurrency(selectedAccount.currentBalance || 0)}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowAccountModal(false);
                      setSelectedAccount(null);
                      setTransactions([]);
                    }}
                    className="px-5 py-3 rounded-xl text-gray-800 dark:text-slate-200 font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    Schließen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Category Transaction Modal */}
          {showTransactionModal && selectedCategory && (
            <div
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
              style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
              onClick={() => {
                setShowTransactionModal(false);
                setSelectedCategory(null);
                setTransactions([]);
              }}
            >
              <div
                className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-4xl flex flex-col overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)', animation: 'slideInFromBottom 0.25s ease' }}
                onClick={(e) => e.stopPropagation()}
              >
                {(() => {
                  const txnIncome = transactions.filter(t => t.isIncome).reduce((sum, t) => sum + Math.abs(t.categoryAmount || 0), 0);
                  const txnExpense = transactions.filter(t => !t.isIncome).reduce((sum, t) => sum + Math.abs(t.categoryAmount || 0), 0);
                  const netAmount = txnIncome - txnExpense;
                  return (
                    <>
                      <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: '1px solid #1e293b' }}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                            style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                          >
                            <TagIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Kategorie · {selectedYear}</div>
                            <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight truncate">{selectedCategory || 'Unkategorisiert'}</h2>
                            <p className="text-gray-600 dark:text-slate-400 text-sm mt-0.5">{transactions.length} {transactions.length === 1 ? 'Buchung' : 'Buchungen'}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setShowTransactionModal(false);
                            setSelectedCategory(null);
                            setTransactions([]);
                          }}
                          className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        >
                          <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                        </button>
                      </div>

                      {transactions.length > 0 && (txnIncome > 0 || txnExpense > 0) && (
                        <div className="px-6 py-4 flex flex-wrap gap-2" style={{ borderBottom: '1px solid #1e293b' }}>
                          {txnIncome > 0 && (
                            <div
                              className="flex items-center gap-2 px-3 py-2 rounded-xl"
                              style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                            >
                              <ArrowUpIcon className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
                              <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{formatCurrency(txnIncome)}</span>
                            </div>
                          )}
                          {txnExpense > 0 && (
                            <div
                              className="flex items-center gap-2 px-3 py-2 rounded-xl"
                              style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
                            >
                              <ArrowDownIcon className="h-4 w-4 text-rose-700 dark:text-rose-300" />
                              <span className="text-sm font-bold text-rose-700 dark:text-rose-300 tabular-nums">{formatCurrency(txnExpense)}</span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="px-6 py-5 overflow-y-auto flex-1">
                        {isLoading ? (
                          <div className="flex flex-col items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-400/30 border-t-primary-400"></div>
                            <p className="text-sm text-gray-600 dark:text-slate-400 mt-3">Lade Buchungen…</p>
                          </div>
                        ) : transactions.length > 0 ? (
                          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #1e293b' }}>
                            {transactions.map((txn, idx) => {
                              const date = new Date(txn.buchungstag);
                              const day = date.toLocaleDateString('de-DE', { weekday: 'short' });
                              const dayNum = date.getDate();
                              const month = date.toLocaleDateString('de-DE', { month: 'short' });
                              const amount = Math.abs(txn.categoryAmount || 0);
                              return (
                                <div
                                  key={txn.id}
                                  onClick={() => setSelectedStatementId(String(txn.id))}
                                  className="px-4 py-3 flex items-center gap-3 cursor-pointer transition"
                                  style={{
                                    borderBottom: idx < transactions.length - 1 ? '1px solid #1e293b' : undefined,
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                  <div
                                    className="flex flex-col items-center justify-center shrink-0"
                                    style={{
                                      width: 56,
                                      height: 64,
                                      borderRadius: 12,
                                      background: txn.isIncome
                                        ? 'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)'
                                        : 'linear-gradient(180deg, rgba(244,63,94,0.16) 0%, rgba(244,63,94,0.04) 100%)',
                                      border: txn.isIncome ? '1px solid rgba(16,185,129,0.25)' : '1px solid rgba(244,63,94,0.25)',
                                    }}
                                  >
                                    <div className={`text-[9px] uppercase tracking-widest font-bold ${txn.isIncome ? 'text-primary-300' : 'text-rose-300'}`}>{day}</div>
                                    <div className="text-xl font-black text-gray-900 dark:text-white leading-none mt-0.5 tabular-nums">{dayNum}</div>
                                    <div className="text-[9px] text-gray-600 dark:text-slate-400 mt-0.5">{month}</div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{txn.buchungstext}</p>
                                    <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-600 dark:text-slate-400">
                                      <span className="font-mono text-gray-500 dark:text-slate-500">#{txn.id}</span>
                                      <span className="flex items-center gap-1">
                                        <CalendarIcon className="h-3 w-3" />
                                        {formatDate(txn.buchungstag)}
                                      </span>
                                      {txn.konto_name && (
                                        <span className="flex items-center gap-1 truncate">
                                          <BuildingLibraryIcon className="h-3 w-3" />
                                          {txn.konto_name}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className={`text-base font-black tabular-nums shrink-0 ${txn.isIncome ? 'text-emerald-300' : 'text-rose-300'}`}>
                                    {txn.isIncome ? '+' : '-'}{formatCurrency(amount)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <div
                              className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                              style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                            >
                              <TagIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                            </div>
                            <p className="text-base font-bold text-gray-900 dark:text-white">Keine Buchungen</p>
                            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">Für diese Kategorie sind noch keine Buchungen erfasst.</p>
                          </div>
                        )}
                      </div>

                      <div
                        className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3"
                        style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
                      >
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Netto</div>
                          <div className={`text-2xl font-black tabular-nums ${netAmount >= 0 ? 'text-primary-400' : 'text-rose-300'}`}>
                            {netAmount >= 0 ? '+' : ''}{formatCurrency(netAmount)}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setShowTransactionModal(false);
                            setSelectedCategory(null);
                            setTransactions([]);
                          }}
                          className="px-5 py-3 rounded-xl text-gray-800 dark:text-slate-200 font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        >
                          Schließen
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Excel Export Modal */}
          <ExcelExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            accounts={accounts}
            allStatements={allStatements}
            isMobile={window.innerWidth < 640}
            isLoading={isLoadingExport}
          />

          {/* CSV Import Modal */}
          {showImportModal && (
            <CSVImporter
              onImportSuccess={() => {
                setShowImportModal(false);
                fetchAllData();
              }}
              onClose={() => setShowImportModal(false)}
            />
          )}

          {/* Mobile FAB: Neue Buchung */}
          {accounts.length > 0 && (
            <button
              onClick={() => setShowAddBookingModal(true)}
              className="sm:hidden fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full flex items-center justify-center transition"
              style={{
                background: '#10b981',
                boxShadow: '0 14px 34px -10px rgba(16,185,129,0.65)',
              }}
              aria-label="Neue Buchung"
            >
              <PlusIcon className="h-6 w-6 text-slate-950" />
            </button>
          )}

          {/* Add Booking Modal */}
          <AddBookingModal
            isOpen={showAddBookingModal}
            onClose={() => setShowAddBookingModal(false)}
            onSuccess={() => {
              fetchAllData();
            }}
          />

          {/* Statement Detail Modal */}
          {selectedStatementId && (
            <StatementDetailModal
              statementId={selectedStatementId}
              onClose={() => setSelectedStatementId(null)}
              onStatementUpdate={() => {
                fetchAllData();
                if (selectedCategory) handleCategoryClick(selectedCategory);
              }}
            />
          )}

          {/* Create Account Modal */}
          {showCreateAccountModal && (
            <div
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
              style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
              onClick={resetCreateForm}
            >
              <div
                className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg flex flex-col overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)', animation: 'slideInFromBottom 0.25s ease' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: '1px solid #1e293b' }}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                    >
                      <WalletIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Konto · Neu</div>
                      <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Neues Konto</h2>
                      <p className="text-gray-600 dark:text-slate-400 text-sm mt-0.5">Lege ein weiteres Vereinskonto an.</p>
                    </div>
                  </div>
                  <button
                    onClick={resetCreateForm}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                  </button>
                </div>

                <form onSubmit={handleCreateAccount} className="flex flex-col flex-1 overflow-hidden">
                  <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Kontoname *</span>
                      <input
                        type="text"
                        value={newAccountName}
                        onChange={(e) => setNewAccountName(e.target.value)}
                        className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                        style={{ colorScheme: 'dark' }}
                        placeholder="z.B. Girokonto Sparkasse"
                        required
                      />
                    </label>

                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Icon</span>
                      <div className="mt-1.5 grid grid-cols-5 sm:grid-cols-6 gap-2">
                        {availableIcons.map(({ id, name, IconComponent }) => (
                          <button
                            type="button"
                            key={id}
                            onClick={() => setNewAccountIcon(id)}
                            title={name}
                            className={`p-2 sm:p-2.5 rounded-xl flex items-center justify-center aspect-square transition`}
                            style={
                              newAccountIcon === id
                                ? { background: '#10b981', color: '#020617', boxShadow: '0 0 0 2px rgba(16,185,129,0.30)' }
                                : { background: 'rgba(15,23,42,0.6)', border: '1px solid #334155', color: '#cbd5e1' }
                            }
                          >
                            <IconComponent className="h-5 w-5" />
                          </button>
                        ))}
                      </div>
                    </div>

                    <label className="block">
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Startbetrag (€)</span>
                      <input
                        type="number"
                        value={newAccountBalance}
                        onChange={(e) => setNewAccountBalance(e.target.value)}
                        className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition tabular-nums bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                        style={{ colorScheme: 'dark' }}
                        placeholder="0,00"
                        step="0.01"
                      />
                    </label>

                    <label className="block">
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Kontotyp</span>
                      <select
                        value={newAccountType}
                        onChange={(e) => setNewAccountType(e.target.value)}
                        className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                        style={{ colorScheme: 'dark' }}
                      >
                        <option value="Bargeldkonto">Bargeldkonto</option>
                        <option value="Bankkonto">Bankkonto</option>
                        <option value="Kreditkarte">Kreditkarte</option>
                        <option value="Depot">Depot</option>
                        <option value="Sonstiges">Sonstiges</option>
                      </select>
                    </label>

                    <div className="pt-4 space-y-2.5" style={{ borderTop: '1px solid #1e293b' }}>
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Standard-Konten</span>
                      <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition hover:bg-white/[0.03]"
                             style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}>
                        <input
                          type="checkbox"
                          checked={newAccountIsGaestekarten}
                          onChange={(e) => setNewAccountIsGaestekarten(e.target.checked)}
                          className="w-5 h-5 rounded text-primary-500"
                          style={{ colorScheme: 'dark' }}
                        />
                        <CreditCardIcon className="h-4 w-4 text-blue-700 dark:text-blue-300" />
                        <span className="text-sm text-gray-800 dark:text-slate-200">Standard für Gästekarten-Transaktionen</span>
                      </label>

                      <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition hover:bg-white/[0.03]"
                             style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}>
                        <input
                          type="checkbox"
                          checked={newAccountIsVerzehrkosten}
                          onChange={(e) => setNewAccountIsVerzehrkosten(e.target.checked)}
                          className="w-5 h-5 rounded text-primary-500"
                          style={{ colorScheme: 'dark' }}
                        />
                        <ShoppingCartIcon className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                        <span className="text-sm text-gray-800 dark:text-slate-200">Standard für Verzehrkosten-Transaktionen</span>
                      </label>
                    </div>
                  </div>

                  <div
                    className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                    style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
                  >
                    <button
                      type="button"
                      onClick={resetCreateForm}
                      className="px-5 py-3 rounded-xl text-gray-800 dark:text-slate-200 font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      disabled={!newAccountName.trim()}
                      className="px-5 py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 font-bold transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="h-5 w-5" />
                      Erstellen
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Account Modal */}
          {showEditAccountModal && editingAccount && (
            <div
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
              style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
              onClick={resetEditForm}
            >
              <div
                className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg flex flex-col overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)', animation: 'slideInFromBottom 0.25s ease' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: '1px solid #1e293b' }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)' }}
                    >
                      <PencilIcon className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-blue-700 dark:text-blue-300 font-bold">Konto · Bearbeiten</div>
                      <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight truncate">{editingAccount.name}</h2>
                      <p className="text-gray-600 dark:text-slate-400 text-sm mt-0.5">Stamm-Daten anpassen</p>
                    </div>
                  </div>
                  <button
                    onClick={resetEditForm}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                  </button>
                </div>

                <form onSubmit={handleEditAccount} className="flex flex-col flex-1 overflow-hidden">
                  <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Kontoname *</span>
                      <input
                        type="text"
                        value={newAccountName}
                        onChange={(e) => setNewAccountName(e.target.value)}
                        className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                        style={{ colorScheme: 'dark' }}
                        placeholder="z.B. Girokonto Sparkasse"
                        required
                      />
                    </label>

                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Icon</span>
                      <div className="mt-1.5 grid grid-cols-5 sm:grid-cols-6 gap-2">
                        {availableIcons.map(({ id, name, IconComponent }) => (
                          <button
                            type="button"
                            key={id}
                            onClick={() => setNewAccountIcon(id)}
                            title={name}
                            className="p-2 sm:p-2.5 rounded-xl flex items-center justify-center aspect-square transition"
                            style={
                              newAccountIcon === id
                                ? { background: '#10b981', color: '#020617', boxShadow: '0 0 0 2px rgba(16,185,129,0.30)' }
                                : { background: 'rgba(15,23,42,0.6)', border: '1px solid #334155', color: '#cbd5e1' }
                            }
                          >
                            <IconComponent className="h-5 w-5" />
                          </button>
                        ))}
                      </div>
                    </div>

                    <label className="block">
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Kontotyp</span>
                      <select
                        value={newAccountType}
                        onChange={(e) => setNewAccountType(e.target.value)}
                        className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                        style={{ colorScheme: 'dark' }}
                      >
                        <option value="Bargeldkonto">Bargeldkonto</option>
                        <option value="Bankkonto">Bankkonto</option>
                        <option value="Kreditkarte">Kreditkarte</option>
                        <option value="Depot">Depot</option>
                        <option value="Sonstiges">Sonstiges</option>
                      </select>
                    </label>

                    <div className="pt-4 space-y-2.5" style={{ borderTop: '1px solid #1e293b' }}>
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Standard-Konten</span>
                      <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition hover:bg-white/[0.03]"
                             style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}>
                        <input
                          type="checkbox"
                          checked={newAccountIsGaestekarten}
                          onChange={(e) => setNewAccountIsGaestekarten(e.target.checked)}
                          className="w-5 h-5 rounded text-primary-500"
                          style={{ colorScheme: 'dark' }}
                        />
                        <CreditCardIcon className="h-4 w-4 text-blue-700 dark:text-blue-300" />
                        <span className="text-sm text-gray-800 dark:text-slate-200">Standard für Gästekarten-Transaktionen</span>
                      </label>

                      <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition hover:bg-white/[0.03]"
                             style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}>
                        <input
                          type="checkbox"
                          checked={newAccountIsVerzehrkosten}
                          onChange={(e) => setNewAccountIsVerzehrkosten(e.target.checked)}
                          className="w-5 h-5 rounded text-primary-500"
                          style={{ colorScheme: 'dark' }}
                        />
                        <ShoppingCartIcon className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                        <span className="text-sm text-gray-800 dark:text-slate-200">Standard für Verzehrkosten-Transaktionen</span>
                      </label>
                    </div>
                  </div>

                  <div
                    className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                    style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
                  >
                    <button
                      type="button"
                      onClick={resetEditForm}
                      className="px-5 py-3 rounded-xl text-gray-800 dark:text-slate-200 font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      disabled={!newAccountName.trim()}
                      className="px-5 py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 font-bold transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PencilIcon className="h-5 w-5" />
                      Speichern
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Confirmation Modal */}
          <ConfirmationModal
            visible={confirmModal.isOpen}
            onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            type={confirmModal.type}
            title={confirmModal.title}
            message={confirmModal.message}
            variant={confirmModal.variant}
            button={confirmModal.variant === 'single' ? {
              text: 'OK',
              onPress: () => {
                confirmModal.onConfirm?.();
                setConfirmModal({ ...confirmModal, isOpen: false });
              }
            } : undefined}
            buttons={confirmModal.variant === 'two' ? {
              cancel: {
                text: 'Abbrechen',
                onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
              },
              confirm: {
                text: 'Bestätigen',
                onPress: () => {
                  confirmModal.onConfirm?.();
                  setConfirmModal({ ...confirmModal, isOpen: false });
                }
              }
            } : undefined}
          />
        </div>
      </div>
    </>
  );
};

export default AccountOverviewEnhanced;
